import { useField } from 'formik';
import type { AddressViewModel } from '@generated';
import { Button } from '~/shared/components/buttons/Button';
import { FieldLabelWithHtml } from '~/shared/forms/controls/base-controls/FieldLabelWithHtml';
import { FieldErrorWithoutAria } from '~/shared/forms/controls/base-controls/FieldError';
import { FieldDescription } from '~/shared/forms/controls/base-controls/FieldDescription';
import { baseControlStyles } from '~/shared/forms/controls/base-controls/util';
import { cn } from '~/shared/components/utils';
import { ZipCodePickerModal } from './ZipCodePickerModal';

export function IncludedZipCodesFormField({
	name,
	addressFieldName,
	label,
	className
}: {
	name: string;
	addressFieldName: string;
	label: string;
	className?: string;
}) {
	const [field, meta, helpers] = useField<string[]>(name);
	const [addressField] = useField<AddressViewModel | null | undefined>(addressFieldName);
	const zipCodes = [...(field.value ?? [])].sort();
	const homeZipCode = addressField.value?.zipCode;
	const errorMessage = meta.touched && typeof meta.error === 'string' ? meta.error : undefined;
	const { base } = baseControlStyles();

	return (
		<div className={cn(base(), className)}>
			<FieldLabelWithHtml className="mb-1" label={label} />
			<div className="flex items-start gap-3">
				<div className="min-h-[32px] flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
					{zipCodes.length ? zipCodes.join(', ') : <span className="text-gray-400">No zip codes selected</span>}
				</div>
				<Button
					appearance="outline"
					intent="neutral"
					label="Edit Zip Codes"
					isDisabled={!homeZipCode}
					renderModal={closeModal => (
						<ZipCodePickerModal
							initialZipCodes={zipCodes}
							homeZipCode={homeZipCode!}
							onApply={x => {
								helpers.setValue(x);
								helpers.setTouched(true);
								closeModal();
							}}
							closeModal={closeModal}
						/>
					)}
				/>
			</div>
			{!homeZipCode && <FieldDescription>Enter an address before editing zip codes.</FieldDescription>}
			{errorMessage && <FieldErrorWithoutAria errorMessage={errorMessage} />}
		</div>
	);
}
